import { BalanceData, Card, CardKind } from "@highmarch/domain";
import { cardClass, cardFace } from "./cardArt";
import { setText } from "./hudPills";

/**
 * The tray's tier-4 parts (M21, docs/TOWERS_V2.md "Tier 4"). The sim deals an Ascend or skill card as an extra card after
 * a hand's own (sim/branches/ascension.ts), so the hand keeps its three slots and the special card sits apart: a gold
 * card pinned above the tray's right corner, glowing, where it reads as a bonus rather than a fourth choice crammed in.
 * Picking it is a pick like any other (its index in the offer); an Ascend card opens the branch picker
 * (branchPicker.ts), a skill card turns the placement bar into LEARN.
 */

/** Whether a card is a tier-4 special card. */
export const isSpecial = (c: Card): boolean => c.kind === CardKind.Ascend || c.kind === CardKind.Skill;

/** The hand's own cards (road, tower, upgrade): the special card, when dealt, is the last and not counted. */
export function handCards(cards: readonly Card[]): number {
  return cards.length > 0 && isSpecial(cards[cards.length - 1]) ? cards.length - 1 : cards.length;
}

/** Sets a button's disabled state and the kit's `.dis` look together, touching the DOM only on a change. */
export function setDisabled(b: HTMLButtonElement, off: boolean): void {
  if (b.disabled !== off) b.disabled = off;
  if (b.classList.contains("dis") !== off) b.classList.toggle("dis", off);
}

/** The special card's button above the tray. */
export class SpecialSlot {
  private readonly button: HTMLButtonElement;
  private index = -1;
  constructor(parent: HTMLElement, pick: (i: number) => void) {
    this.button = document.createElement("button");
    this.button.className = "card special hidden";
    this.button.dataset.guide = "special";
    this.button.onclick = () => { if (this.index >= 0) pick(this.index); };
    this.button.addEventListener("animationend", () => this.button.classList.remove("deal"));
    parent.append(this.button);
  }

  /** A new hand: shows its special card (the last), or hides the slot. `anim` is the deal class (" deal" or ""). */
  deal(balance: BalanceData, cards: readonly Card[], anim: string): void {
    const last = cards.length > 0 ? cards[cards.length - 1] : null;
    if (last === null || !isSpecial(last)) { this.clear(); return; }
    this.index = cards.length - 1;
    this.button.className = `card ${cardClass(balance, last)}${anim}`;
    this.button.innerHTML = cardFace(balance, last, () => 0) + `<span class="spark" aria-hidden="true"></span>`;
  }

  /** Hidden while a card is being placed (the tray turns into the placement bar), shown again with the hand. */
  toggle(show: boolean): void { this.button.classList.toggle("off", !show); }

  clear(): void { this.index = -1; this.button.className = "card special hidden"; this.button.innerHTML = ""; }
}

/** The placement bar's parts the special cards write. */
export interface PlacingParts { readonly ribbon: HTMLElement; readonly title: HTMLElement; readonly hint: HTMLElement; readonly place: HTMLButtonElement; readonly rotate: HTMLButtonElement }

/**
 * The placement bar for a picked special card. Ascend: the branch picker does the choosing, so the bar only says so
 * (BACK still returns the card). Skill: the skill's name and what it does, and LEARN spends the card.
 */
export function specialPlacing(p: PlacingParts, balance: BalanceData, card: Card, hint: (el: HTMLElement, text: string) => void): void {
  p.rotate.classList.add("hidden");
  if (card.kind === CardKind.Ascend) {
    setText(p.ribbon, "Ascend");
    setText(p.title, `${balance.towers[card.index].name} → tier IV`);
    hint(p.hint, "Choose a branch above");
    p.place.classList.add("hidden");
    return;
  }
  const s = balance.branches.skills[card.index], b = balance.branches.branches[s.branchIndex];
  setText(p.ribbon, "New skill");
  setText(p.title, `${s.name} · ${b.name}`);
  hint(p.hint, s.description);
  setText(p.place, "LEARN");
  p.place.classList.remove("hidden");
  setDisabled(p.place, false);
  p.place.classList.add("ready");
}
